import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAtom } from "jotai";
import { gbState } from "../../../components/shared/GlobalState";
import {
  FaSave,
  FaUpload,
  FaNewspaper,
  FaFileAlt,
  FaRegEdit,
} from "react-icons/fa";
import { HiOutlineDocumentText, HiOutlinePhotograph } from "react-icons/hi";
import { IoCloseCircleOutline } from "react-icons/io5";

const NewsAdd = ({ currentRecord, setModel, getDataFromServer }) => {
  const navigate = useNavigate();
  // Error Logging Service & Sanitize Input
  const [{ errorLogApi, placeHolderText, user }] = useAtom(gbState);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = React.useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    _id: currentRecord?._id ?? null,
    title: currentRecord?.title ?? null,
    description: currentRecord?.description ?? null,
    church_id: user?.church_id,
    createdBy: user?._id,
  });

  const isImageFile = (filename) => {
    const imageExtensions = [".jpg", ".jpeg", ".png", ".gif", ".bmp", ".webp"];
    return imageExtensions.some((ext) => filename?.toLowerCase().endsWith(ext));
  };

  // If editing, load the existing image preview
  useEffect(() => {
    if (currentRecord?.image) {
      if (isImageFile(currentRecord.image)) {
        setImagePreview(
          `${process.env.REACT_APP_REPORTS_DOWNLOAD_URL}uploads/news/${currentRecord.image}`
        );
      } else {
        // Handle non-image files if needed
        setImagePreview(null);
      }
    }
  }, [currentRecord]);
  const handleChange = ({ currentTarget: input }) => {
    setFormData({
      ...formData,
      [input.name]: input.value,
    });
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedImage(file);
      // Create a preview URL for the selected image
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
    }
  };

  const clearImageSelection = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleEvent = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    let userToken = localStorage.getItem("token");

    // Create FormData object for proper multipart/form-data handling
    const formDataObj = new FormData();

    // Add all form fields to FormData
    Object.keys(formData).forEach((key) => {
      if (formData[key] !== null) {
        formDataObj.append(key, formData[key]);
      }
    });

    // Add the image file if selected
    if (selectedImage) {
      formDataObj.append("image", selectedImage);
    }

    let config = {};
    if (formData._id) {
      config = {
        method: "patch",
        url: `${process.env.REACT_APP_API_URL}/news/update/${formData._id}`,
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
        data: formDataObj,
      };
    } else {
      config = {
        method: "post",
        url: `${process.env.REACT_APP_API_URL}/news/add`,
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
        data: formDataObj,
      };
    }

    axios(config)
      .then(function (response) {
        toast.success(
          formData._id
            ? "Newsletter updated successfully."
            : "Newsletter created successfully."
        );
        // Close the modal after saving
        navigate("/newsletters");
        getDataFromServer();
        setModel(false);
      })
      .catch(function (error) {
        toast.error(
          error.response?.data
            ? JSON.stringify(error.response.data)
            : "An error occurred"
        );
        errorLogApi(error, "CreateNewsChild", "/newsletters");
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };
  return (
    <div className="flex justify-center items-center w-full bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="overflow-hidden w-full max-w-4xl bg-white rounded-xl shadow-lg">
        <form
          onSubmit={(e) => handleEvent(e)}
          autoComplete="off"
          className="flex flex-col h-full"
        >
          {/* Header Section */}
          <div className="flex justify-between items-center p-6 border-b border-gray-200">
            <h2 className="flex items-center text-2xl font-bold text-gray-900">
              <FaRegEdit className="mr-3 text-blue-600" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                {formData._id ? "Edit Newsletter" : "Create Newsletter"}
              </span>
            </h2>
            <button
              className="flex items-center px-5 py-2.5 space-x-2 text-white bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg shadow-md transition duration-300 ease-in-out hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-70"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <FaSave className="text-sm" />
                  <span>Save</span>
                </>
              )}
            </button>
          </div>

          {/* Form Content */}
          <div className="flex-grow p-6 space-y-6">
            {/* Image Upload and Preview Section */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div className="space-y-4">
                <label
                  htmlFor="news_image"
                  className="block text-sm font-medium text-gray-700 flex items-center"
                >
                  <HiOutlinePhotograph className="mr-1.5 text-lg text-blue-500" />
                  Upload Newsletter Image
                </label>
                <div className="relative">
                  <input
                    id="news_image"
                    name="news_image"
                    type="file"
                    className="sr-only"
                    onChange={handleImageChange}
                    accept="image/gif, image/jpeg, image/png, application/pdf"
                    required={!formData._id && !selectedImage}
                    ref={fileInputRef}
                  />
                  {!imagePreview ? (
                    <div
                      className="flex justify-center items-center p-6 h-40 rounded-lg border-2 border-gray-300 border-dashed transition-colors duration-300 cursor-pointer hover:border-blue-500 bg-gray-50"
                      onClick={() => fileInputRef.current.click()}
                    >
                      <div className="space-y-2 text-center">
                        <FaUpload className="mx-auto w-8 h-8 text-gray-400" />
                        <div className="text-sm text-gray-600">
                          <span className="font-medium text-blue-600 hover:text-blue-500">
                            Click to upload
                          </span>{" "}
                          or drag and drop
                        </div>
                        <p className="text-xs text-gray-500">
                          PNG, JPG, GIF up to 10MB
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="relative h-40 rounded-lg overflow-hidden border border-gray-300 group">
                      {isImageFile(currentRecord?.image) ? (
                        <img
                          src={imagePreview}
                          alt="Newsletter preview"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="flex flex-col items-center justify-center h-full bg-gray-50">
                          <svg
                            className="w-12 h-12 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                            />
                          </svg>
                          <span className="mt-2 text-sm text-gray-500">
                            {currentRecord?.image}
                          </span>
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={clearImageSelection}
                        className="absolute top-2 right-2 p-1 rounded-full bg-white bg-opacity-75 text-gray-700 hover:text-red-500 transition-colors duration-200 shadow-sm"
                        title="Remove image"
                      >
                        <IoCloseCircleOutline className="w-5 h-5" />
                      </button>
                      <div
                        className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 flex items-center justify-center transition-all duration-200 cursor-pointer"
                        onClick={() => fileInputRef.current.click()}
                      >
                        <div className="p-2 rounded-full bg-white bg-opacity-0 group-hover:bg-opacity-75 transition-all duration-200">
                          <FaUpload className="w-5 h-5 text-transparent group-hover:text-gray-700" />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                {selectedImage && (
                  <div className="flex items-center text-xs text-gray-500">
                    <HiOutlineDocumentText className="mr-1" />
                    <span className="truncate max-w-xs">
                      {selectedImage.name}
                    </span>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <label
                  htmlFor="title"
                  className="block text-sm font-medium text-gray-700 flex items-center"
                >
                  <FaNewspaper className="mr-1.5 text-blue-500" />
                  Newsletter Title
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="title"
                    id="title"
                    className="block w-full px-4 py-3 rounded-lg border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                    placeholder="Enter a descriptive title"
                    required
                    onChange={handleChange}
                    value={formData.title || ""}
                  />
                </div>

                <label
                  htmlFor="description"
                  className="block text-sm font-medium text-gray-700 flex items-center mt-4"
                >
                  <FaFileAlt className="mr-1.5 text-blue-500" />
                  Description
                </label>
                <div className="relative">
                  <textarea
                    id="description"
                    name="description"
                    rows={5}
                    className="block w-full px-4 py-3 rounded-lg border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                    placeholder="Provide details about this newsletter..."
                    required
                    onChange={handleChange}
                    value={formData.description || ""}
                  />
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewsAdd;
